import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { ErrorFallback } from '@components/ErrorFallback';
import { Loader } from '@components/Loader';
import { PackageByVendor } from '@graphql';
import { usePrepop } from '@hooks';
import { routeTree } from '@route/generated';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { FeatureFlagKeys } from '@utils/constants';
import { getTrackingId, redirectToApplicationForm } from '@utils/helper';

const router = createRouter({
  routeTree,
  context: { authentication: undefined! },
  trailingSlash: 'preserve',
  defaultErrorComponent: ({ error, reset }) => {
    return <ErrorFallback error={error} resetErrorBoundary={reset} />;
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface HistoryState {
    vendorPackage?: PackageByVendor;
  }
}

export const FormPage = () => {
  const trackingId = getTrackingId();
  const { loading, data: quote, error } = usePrepop({ trackingId });
  const authentication = useAuth0();

  const flags = useFlags();
  const isUniversalLoginEnabled = flags[FeatureFlagKeys.UniversalLogin];

  /**
   * If there's no tracking ID present (+ enabled feature flag),
   * redirect to universal login flow
   */
  if (trackingId.trim() === '' && isUniversalLoginEnabled) {
    if (window.location.pathname.startsWith('/connect/login') || authentication.verificationCompleted) {
      return <RouterProvider router={router} context={{ authentication }} />;
    } else {
      const searchParams = window.location.search;
      window.location.replace(`/connect/login${searchParams}`);
    }
  }

  if (loading) {
    return <Loader />;
  }

  if (error) {
    throw error;
  }

  if (!quote?.opportunity?.id || !quote?.enterprisePartnerId || !quote?.enterprisePartnerConfig?.packageEnabled) {
    redirectToApplicationForm(trackingId);
    return null;
  }

  return <RouterProvider router={router} context={{ authentication }} />;
};
